import { consequences, displayOf, entityTitle, timeAtPlacement, withPlacement, withSceneTime, type Consequences, type Issue, type Placement as TimelinePlace, type StoryCtx, type StoryNode } from '@dispositio/core';
import { de } from '../i18n/de';
import { toast } from '../state/toast';
import { issueTarget } from './issues';
import { placementPatch, type Placement } from './outline';

/**
 * Folgen sehen, bevor man loslässt (PF 55, core `consequences`): beim Ziehen auf der Zeitlinie bzw. beim Umsortieren im Binder und in der
 * Erzähllinie, was danach neu nicht passt, was sich auflöst und welche Szenen mitwandern – als kurze Zeilen, und nach dem Loslassen als
 * Meldung mit „Zeigen“. Die letzte Rechnung wird gemerkt: beim Ziehen bleibt die Stelle oft dieselbe.
 */

let last: { ctx: StoryCtx; key: string; result: Consequences | null } | null = null;
const remembered = (ctx: StoryCtx, key: string, run: () => Consequences | null): Consequences | null => {
  if (last && last.ctx === ctx && last.key === key) return last.result;
  const result = run();
  last = { ctx, key, result };
  return result;
};

/** Folgen, wenn die angedockte Szene `sceneId` an die Stelle `place` ihrer Zeitlinie käme (ihre Zeit dort) */
export function moveConsequences(ctx: StoryCtx, sceneId: string, place: TimelinePlace): Consequences | null {
  const scene = ctx.nodes.get(sceneId);
  const tl = scene?.parentId ? ctx.nodes.get(scene.parentId) : undefined;
  if (!scene || !tl?.timeline || (scene.kind !== 'event' && scene.kind !== 'period')) return null;
  const time = timeAtPlacement(tl.timeline, scene.kind, place, scene.time);
  if (!time) return null;
  return remembered(ctx, `time:${sceneId}:${JSON.stringify(time)}`, () => {
    const after = withSceneTime(ctx, sceneId, time);
    return consequences(ctx, after.ctx, after.along);
  });
}

/** Folgen, wenn Szene bzw. Kapitel `node` im Manuskript an die Stelle `placement` käme; null, wenn sich nichts ändert */
export function placeConsequences(ctx: StoryCtx, node: StoryNode, placement: Placement | null): Consequences | null {
  const patch = placementPatch(node, placement);
  if (!patch) return null;
  return remembered(ctx, `place:${node.id}:${JSON.stringify(patch)}`, () => consequences(ctx, withPlacement(ctx, node.id, patch)));
}

const titleOf = (ctx: StoryCtx, id: string): string => {
  const n = ctx.nodes.get(id);
  return (n && displayOf(ctx, n).title) || de.consequences.untitled;
};

/** Ein Widerspruch in wenigen Worten */
function shortIssue(ctx: StoryCtx, i: Issue): string {
  const t = de.consequences.issue;
  const title = titleOf(ctx, i.nodeId);
  switch (i.code) {
    case 'time-mismatch':
      return t.timeMismatch(title);
    case 'time-order':
      return t.timeOrder(title);
    case 'time-wording':
      return t.timeWording(title);
    case 'place-mismatch':
      return t.placeMismatch(title);
    case 'pov-mismatch':
      return t.povMismatch(title);
    case 'life-text':
    case 'life-pov':
    case 'life-change':
      return t.notAlive(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'item-holder':
      return t.holder(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'place-clash':
      return t.placeClash(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'knowledge-leak':
      return t.knowledgeLeak(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'age-text':
      return t.ageText(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'travel-time':
      return t.travel(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'trait-text':
      return t.trait(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    case 'item-use':
      return t.itemUse(i.entityId ? entityTitle(ctx, i.entityId) : '', title);
    default:
      return i.message;
  }
}

export interface ConsequenceLine {
  kind: 'added' | 'resolved' | 'along';
  text: string;
}

/** Die Folgen als Zeilen (neu, aufgelöst, mitwandernd); leer, wenn sich nichts ändert */
export function consequenceLines(ctx: StoryCtx, c: Consequences | null): ConsequenceLine[] {
  if (!c) return [];
  const C = de.consequences;
  const out: ConsequenceLine[] = [
    ...c.added.map((i): ConsequenceLine => ({ kind: 'added', text: shortIssue(ctx, i) })),
    ...c.resolved.map((i): ConsequenceLine => ({ kind: 'resolved', text: C.resolved(shortIssue(ctx, i)) })),
  ];
  if (c.along.length > 0) out.push({ kind: 'along', text: C.along(c.along.map((id) => `„${titleOf(ctx, id)}“`)) });
  return out;
}

/**
 * Nach dem Loslassen (die Änderung ist gespeichert): eine Meldung, wenn danach etwas neu nicht passt – mit „Zeigen“, das zur ersten Stelle
 * führt –, sonst nur, wenn Szenen mitgewandert sind. Ohne Folgen nichts.
 */
export function announceConsequences(ctx: StoryCtx, c: Consequences | null, goTo: (target: ReturnType<typeof issueTarget>) => void): void {
  if (!c) return;
  const C = de.consequences;
  const along = c.along.length > 0 ? ` ${C.alongDone(c.along.length)}` : '';
  if (c.added.length > 0) {
    const first = c.added[0]!;
    toast.action(`${C.after(c.added.length)}: ${shortIssue(ctx, first)}${c.added.length > 1 ? ' …' : ''}.${along}`, C.show, () => goTo(issueTarget(first)));
  } else if (c.along.length > 0) toast.info(along.trim());
}
